import { IALA_MARKS } from "@nav-games/iala";
import type { Direction } from "@nav-games/soundings";

import { BuoyFigure } from "./BuoyFigure";

/// Shared cell glyphs for the Soundings games (single- and two-player).
/// The cardinal mark reuses the real IALA `BuoyFigure`; rocks and water
/// are small inline SVGs so they read at tiny grid-cell sizes.

/// Fills a (position:relative) grid-cell so percentage-sized glyphs
/// scale with the cell and stay centred.
export function CellFill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </span>
  );
}

const CARDINAL_MARK: Record<Direction, string> = {
  N: "north_cardinal",
  E: "east_cardinal",
  S: "south_cardinal",
  W: "west_cardinal",
};

/// An IALA cardinal buoy filling its cell. Cardinals are
/// region-independent, so Region A is fine.
export function CardinalBuoy({ direction }: { direction: Direction }) {
  return <BuoyFigure mark={IALA_MARKS[CARDINAL_MARK[direction]]!} region="A" fill />;
}

/// A rock / drying-height hazard. `aground` tints it red — the player
/// sailed onto it; otherwise it's a neutral grey rock spotted via its
/// cardinal.
export function RockGlyph({ aground = false }: { aground?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="78%"
      height="78%"
      aria-hidden
      style={{ display: "block" }}
    >
      <polygon
        points="2,21 7,11 11,16 15,7 19,13 22,21"
        fill={aground ? "#b8302a" : "#6b7280"}
        stroke={aground ? "#7a1f1b" : "#374151"}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/// A small blue wave for revealed open water.
export function WaveGlyph() {
  return (
    <svg
      viewBox="0 0 24 12"
      width="68%"
      height="68%"
      aria-hidden
      style={{ display: "block" }}
    >
      <path
        d="M1 7 q2.5 -4 5 0 t5 0 t5 0 t5 0"
        fill="none"
        stroke="#1e6091"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.65"
      />
    </svg>
  );
}
